import { useCallback, useEffect, useState } from 'react';
import { instance } from '../../../axios/AxiosInstance';
import type { ListResponse } from './singleTableListSupport';
import { settingsListApiError } from './singleTableListSupport';

const PATH = 'category';

export interface LoadCategoryListOptions {
  /** When true, skip full-panel loading state (e.g. after save or delete). */
  silent?: boolean;
}

export function useCategoryList<T>() {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async (options?: LoadCategoryListOptions) => {
    const silent = options?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const { data } = await instance.get<ListResponse>(PATH);
      setRows(Array.isArray(data.data) ? (data.data as T[]) : []);
    } catch (e: unknown) {
      if (!silent) {
        setError(settingsListApiError(e, 'Жагсаалт ачааллахад алдаа гарлаа.'));
        setRows([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const deleteById = useCallback(
    async (id: string) => {
      setDeletingId(id);
      setError(null);
      try {
        await instance.delete(PATH, { data: { id } });
        await load({ silent: true });
      } catch (e: unknown) {
        setError(settingsListApiError(e, 'Устгахад алдаа гарлаа.'));
      } finally {
        setDeletingId(null);
      }
    },
    [load],
  );

  return { rows, loading, error, load, deleteById, deletingId };
}
